/* global gsap */
// UTILS
// ===========================

// Map number x from range [a, b] to [c, d]
const map = (x, a, b, c, d) => (x - a) * (d - c) / (b - a) + c;

// Linear interpolation
const lerp = (a, b, n) => (1 - n) * a + n * b;

let currentBreakpoint;
const calcWindowSize = () => {
	currentBreakpoint = getComputedStyle(document.documentElement).getPropertyValue('--bs-breakpoint-lg');
	return {width: window.innerWidth, height: window.innerHeight};
};

const getRandomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

// Gets the mouse position
const getMousePos = e => {
	return {
		x : e.clientX,
		y : e.clientY
	};
};

const grid_wrapper = document.querySelector('.grid-3d-wrapper') || false;

/**
 * Gets computed translate values
 * @param {HTMLElement} element
 * @returns {Object}
 */
const getTranslateValues = element => {
	const style = window.getComputedStyle(element);
	const matrix = style['transform'] || style.webkitTransform || style.mozTransform;

	// No transform property. Simply return 0 values.
	if (matrix === 'none' || typeof matrix === 'undefined') {
		return {
			x: 0,
			y: 0,
			z: 0
		};
	}

	// Can either be 2d or 3d transform
	const matrixType = matrix.includes('3d') ? '3d' : '2d';
	const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');

	// 2d matrices have 6 values
	// Last 2 values are X and Y.
	// 2d matrices does not have Z value.
	if (matrixType === '2d') {
		return {
			x: matrixValues[4],
			y: matrixValues[5],
			z: 0
		};
	}

	// 3d matrices have 16 values
	// The 13th, 14th, and 15th values are X, Y, and Z
	if (matrixType === '3d') {
		return {
			x: matrixValues[12],
			y: matrixValues[13],
			z: matrixValues[14]
		};
	}
};



// Calculate the viewport size
let winsize = calcWindowSize();
if (window.innerWidth >= currentBreakpoint) {
	window.addEventListener('resize', () => winsize = calcWindowSize());
}

// Track the mouse position
let mousepos = {x: winsize.width/2, y: winsize.height/2};
if (window.innerWidth >= currentBreakpoint && grid_wrapper) {
	grid_wrapper.addEventListener('mousemove', ev => mousepos = getMousePos(ev));
}

// Classes
// ============================

class MagneticFx {
	constructor(el) {
		// DOM elements
		this.DOM = {el: el};
		// amounts the element will translated
		this.renderedStyles = {
			tx: {previous: 0, current: 0, amt: 0.04},
			ty: {previous: 0, current: 0, amt: 0.04}
		};
		// calculate size/position
		this.calculateSizePosition();
		// init events
		this.initEvents();
	}

	calculateSizePosition() {
		// current scroll
		this.scrollVal = {x: window.scrollX, y: window.scrollY};
		// size/position
		this.rect = this.DOM.el.getBoundingClientRect();
	}

	initEvents() {
		window.addEventListener('resize', () => this.calculateSizePosition());

		this.DOM.el.addEventListener('mouseenter', () => {
			if (window.innerWidth >= currentBreakpoint) {
				this.hoverTimeout = setTimeout(() => {
					// set to last translated values after hovering out
					const {x, y} = getTranslateValues(this.DOM.el);
					this.renderedStyles['tx'].previous = x;
					this.renderedStyles['ty'].previous = y;
					// start the render loop animation (rAF)
					this.loopRender();
				}, 10);
			}
		});
		this.DOM.el.addEventListener('mouseleave', () => {
			if ( this.hoverTimeout ) {
				clearTimeout(this.hoverTimeout);
			}
			// stop the render loop animation (rAF)
			this.stopRendering();
		});
	}

	// start the render loop animation (rAF)
	loopRender() {
		if ( !this.requestId ) {
			this.requestId = requestAnimationFrame(() => this.render());
		}
	}

	// stop the render loop animation (rAF)
	stopRendering() {
		if ( this.requestId ) {
			window.cancelAnimationFrame(this.requestId);
			this.requestId = undefined;
		}
	}

	render() {
		this.requestId = undefined;

		if (window.innerWidth >= currentBreakpoint) {

			const scrollDiff = {
				x: this.scrollVal.x - window.scrollX,
				y: this.scrollVal.y - window.scrollY
			};

			// new values for the translations
			this.renderedStyles['tx'].current = (mousepos.x - (scrollDiff.x + this.rect.left + this.rect.width / 2)) * .3;
			this.renderedStyles['ty'].current = (mousepos.y - (scrollDiff.y + this.rect.top + this.rect.height / 2)) * .3;

			for (const key in this.renderedStyles) {
				this.renderedStyles[key].previous = lerp(this.renderedStyles[key].previous, this.renderedStyles[key].current, this.renderedStyles[key].amt);
			}

			gsap.set(this.DOM.el, {
				x: this.renderedStyles['tx'].previous,
				y: this.renderedStyles['ty'].previous
			});

			this.loopRender();

		}
	}
}

class GridItem {
	constructor(el) {
		this.DOM = {el: el};
		// the inner image
		this.DOM.image = this.DOM.el.querySelector('.grid__item-img');
		// the title that will appear next to the mouse cursor when hovering
		this.title = this.DOM.el.dataset.title;
		// amounts to move in each axis when moving the cursor
		this.translationVals = {x: 0, y: 0};
		this.rotationVals = {x: 0, y: 0};
		// get random start and end translation/rotation boundaries
		// translation:
		this.xstart = getRandomNumber(70,100);
		this.ystart = getRandomNumber(40,65);

		// rotation:
		// this.rxstart = 5;
		// this.rystart = 10;
		this.rxstart = 0;
		this.rystart = 0;
		// magnetic effect on the image:
		// when hovering on the image, the image will follow the mouse movement
		this.magneticFx = new MagneticFx(this.DOM.image);
		// initial style/position
		this.layout();
		// start the rAF render function (translate and rotate the item as we move the mouse)
		this.loopTransformAnimation();
	}

	// initial position on the grid
	// set the rotation and the translationZ
	layout() {

		if (window.innerWidth >= currentBreakpoint) {
			const rect = this.DOM.el.getBoundingClientRect();

			// check if the element is position on the left/top side of the viewport
			this.isLeft = rect.left + rect.width / 2 < winsize.width / 2;
			this.isTop = rect.top + rect.height / 2 < winsize.height / 2;

			this.rY = this.isLeft ?
				map(rect.left + rect.width / 2, 0, winsize.width / 2, this.rystart, 0) :
				map(rect.left + rect.width / 2, winsize.width / 2, winsize.width, 0, -this.rystart);
			this.rX = this.isTop ?
				map(rect.top + rect.height / 2, 0, winsize.height / 2, -this.rxstart, 0) :
				map(rect.top + rect.height / 2, winsize.height / 2, winsize.height, 0, this.rxstart);
			this.tZ = this.isLeft ?
				map(rect.left + rect.width / 2, 0, winsize.width / 2, -200, -600) :
				map(rect.left + rect.width / 2, winsize.width / 2, winsize.width, -600, -200);

			gsap.set(this.DOM.el, {
				rotationX: this.rX,
				rotationY: this.rY,
				z: this.tZ
			});
		}
	}

	onMouseEnter() {
		if (window.innerWidth >= currentBreakpoint) {
			this.hoverTimeout = setTimeout(() => {
				//this.stopTransformAnimation();

				if (this.timelineHoverOut) this.timelineHoverOut.kill();

				this.timelineHoverIn = gsap.timeline()
					.addLabel('start', 0)
					.to(this.DOM.image, {
						duration: 0.8,
						ease: 'expo',
						scale: 1.1
					}, 'start');

			}, 10);
		}
	}

	onMouseLeave() {
		//this.loopTransformAnimation();

		if (window.innerWidth >= currentBreakpoint) {
			if (this.hoverTimeout) {
				clearTimeout(this.hoverTimeout);
			}

			if (this.timelineHoverIn) this.timelineHoverIn.kill();

			this.timelineHoverOut = gsap.timeline()
				.to(this.DOM.image, {
					duration: 1,
					ease: 'power4',
					x: 0,
					y: 0,
					scale: 1
				});
		}
	}

	loopTransformAnimation() {
		if ( !this.requestId ) {
			this.requestId = requestAnimationFrame(() => this.move());
		}
	}

	// stop the render loop animation (rAF)
	stopTransformAnimation() {
		if ( this.requestId ) {
			window.cancelAnimationFrame(this.requestId);
			this.requestId = undefined;
		}
	}

	// translate/rotate the grid items as we move the mouse
	move() {
		this.requestId = undefined;

		// Calculate the amount to move.
		// Using linear interpolation to smooth things out.
		// Translation values will be in the range of [-start, start] for a cursor movement from 0 to the window's width/height
		this.translationVals.x = lerp(this.translationVals.x, map(mousepos.x, 0, winsize.width, -this.xstart, this.xstart), 0.04);
		this.translationVals.y = lerp(this.translationVals.y, map(mousepos.y, 0, winsize.height, -this.ystart, this.ystart), 0.04);
		// same for the rotations
		this.rotationVals.x = this.isTop ? lerp(this.rotationVals.x, map(mousepos.y, 0, winsize.height/2, this.rxstart, 0), 0.04) : lerp(this.rotationVals.x, map(mousepos.y, winsize.height/2, winsize.height, 0, -this.rxstart), 0.04);
		this.rotationVals.y = this.isLeft ? lerp(this.rotationVals.y, map(mousepos.x, 0, winsize.width/2, -this.rystart, 0), 0.04) : lerp(this.rotationVals.y, map(mousepos.x, winsize.width/2, winsize.width, 0, this.rystart), 0.04);

		gsap.set(this.DOM.el, {
			x: -this.translationVals.x,
			y: -this.translationVals.y,
			rotationX: -this.rX-this.rotationVals.x,
			rotationY: -this.rY-this.rotationVals.y
		});

		this.loopTransformAnimation();
	}
}

class Grid {
	constructor(el) {
		this.DOM = {el: el};

		this.gridItems = [];
		this.DOM.items = [...this.DOM.el.querySelectorAll('.grid__item')];
		this.DOM.items.forEach(item => {
			this.gridItems.push(new GridItem(item));
		});

		this.showItems();
		this.initEvents();
	}

	// Initial animation to scale up and fade in the items
	showItems() {
		gsap.set(this.DOM.items, {scale: 1, opacity: 1}, 0);
	}

	initEvents() {
		for(const item of this.gridItems) {
			item.DOM.image.addEventListener('mouseenter', () => {
				item.onMouseEnter();
			});

			item.DOM.image.addEventListener('mouseleave', () => {
				item.onMouseLeave();
			});

			item.DOM.el.addEventListener('click', () => {
				// ev.preventDefault();
			});
		}
	}
}

const gridElements = document.querySelectorAll('.grid-3d');

if (gridElements) {
	[...gridElements].forEach((grid) => new Grid(grid));
}

